import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const STATUS_STYLES = {
  installed: { bg: 'rgba(78,204,163,0.15)', color: '#4ecca3', label: 'Installed' },
  available: { bg: 'rgba(78,204,163,0.15)', color: '#4ecca3', label: 'Available' },
  active: { bg: 'rgba(78,204,163,0.15)', color: '#4ecca3', label: 'Active' },
  not_installed: { bg: 'rgba(248,113,113,0.15)', color: '#f87171', label: 'Not Installed' },
  error: { bg: 'rgba(248,113,113,0.15)', color: '#f87171', label: 'Error' },
  inactive: { bg: 'rgba(138,154,149,0.15)', color: '#8a9a95', label: 'Inactive' },
};

const SystemModulesView = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [updateMsg, setUpdateMsg] = useState('');

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/system-info`);
      if (res.ok) setInfo(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleUpdate = async (pkgName) => {
    setUpdating(pkgName);
    setUpdateMsg('');
    try {
      const res = await fetch(`${API}/api/system-info/update-package`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: pkgName }),
      });
      const json = await res.json();
      if (json.success) {
        setUpdateMsg(`${pkgName} updated successfully.`);
        fetchInfo();
      } else {
        setUpdateMsg(`Failed: ${json.error || json.output}`);
      }
    } catch (e) {
      setUpdateMsg(`Error: ${e.message}`);
    }
    setUpdating(null);
  };

  if (loading || !info) {
    return <div className="def-list-view"><div className="def-list-header"><h1>System Modules</h1><p className="def-list-subtitle">Loading...</p></div></div>;
  }

  const StatusBadge = ({ status }) => {
    const s = STATUS_STYLES[status] || STATUS_STYLES.error;
    return <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600 }}>{s.label}</span>;
  };

  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>System Modules</h1>
          <p className="def-list-subtitle">
            Installed packages, AI models, services, and database status.
          </p>
        </div>
        <button className="btn-ghost" onClick={fetchInfo}>Refresh</button>
      </div>

      {updateMsg && (
        <div className={`download-msg ${updateMsg.startsWith('Failed') || updateMsg.startsWith('Error') ? 'error' : 'success'}`} style={{ margin: '0 16px' }}>
          {updateMsg}
          <button className="btn-icon" onClick={() => setUpdateMsg('')}>&times;</button>
        </div>
      )}

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Platform */}
        <div className="def-section">
          <h3>Platform</h3>
          <div className="kpi-grid">
            <div className="kpi-card"><span className="kpi-value" style={{ fontSize: 16 }}>{info.platform?.system} {info.platform?.release}</span><span className="kpi-label">Operating System</span></div>
            <div className="kpi-card"><span className="kpi-value" style={{ fontSize: 16 }}>Python {info.platform?.python}</span><span className="kpi-label">Python Version</span></div>
            <div className="kpi-card"><span className="kpi-value" style={{ fontSize: 16 }}>{info.platform?.node}</span><span className="kpi-label">Node.js</span></div>
            <div className="kpi-card"><span className="kpi-value" style={{ fontSize: 16 }}>{info.platform?.machine}</span><span className="kpi-label">Architecture</span></div>
          </div>
        </div>

        {/* Database */}
        <div className="def-section">
          <h3>Database</h3>
          <div className="kpi-grid">
            <div className="kpi-card"><span className="kpi-value" style={{ fontSize: 14, color: '#4ecca3' }}>{info.database?.postgresql}</span><span className="kpi-label">PostgreSQL</span></div>
            <div className="kpi-card"><span className="kpi-value" style={{ fontSize: 16 }}>TimescaleDB {info.database?.timescaledb || 'N/A'}</span><span className="kpi-label">Extension</span></div>
            <div className="kpi-card"><span className="kpi-value">{info.database?.db_size}</span><span className="kpi-label">Database Size</span></div>
            <div className="kpi-card"><span className="kpi-value">{(info.database?.pv_modules || 0).toLocaleString()}</span><span className="kpi-label">PV Modules</span></div>
            <div className="kpi-card"><span className="kpi-value">{(info.database?.pv_inverters || 0).toLocaleString()}</span><span className="kpi-label">PV Inverters</span></div>
            <div className="kpi-card"><span className="kpi-value">{info.database?.conductors || 0}</span><span className="kpi-label">Conductors</span></div>
          </div>
        </div>

        {/* Services */}
        <div className="def-section">
          <h3>Services</h3>
          <div className="conductor-table-wrap">
            <table className="conductor-table">
              <thead><tr><th>Service</th><th>Description</th><th>Status</th></tr></thead>
              <tbody>
                {info.services?.map(s => (
                  <tr key={s.name}>
                    <td className="conductor-name">{s.label}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{s.description}</td>
                    <td><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Python Modules */}
        <div className="def-section">
          <h3>Python Packages</h3>
          <div className="conductor-table-wrap">
            <table className="conductor-table">
              <thead><tr><th>Package</th><th>Description</th><th>Version</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {info.key_modules?.map(m => (
                  <tr key={m.name}>
                    <td>
                      <div className="conductor-name">{m.label}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono' }}>{m.name}</div>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{m.description}</td>
                    <td className="mono">{m.version || '—'}</td>
                    <td><StatusBadge status={m.status} /></td>
                    <td>
                      {m.status === 'installed' && (
                        <button
                          className="btn-card"
                          onClick={() => handleUpdate(m.name)}
                          disabled={updating === m.name}
                        >
                          {updating === m.name ? 'Updating...' : 'Update'}
                        </button>
                      )}
                      {m.status === 'not_installed' && (
                        <button
                          className="btn-card"
                          onClick={() => handleUpdate(m.name)}
                          disabled={updating === m.name}
                        >
                          {updating === m.name ? 'Installing...' : 'Install'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ollama Models */}
        <div className="def-section">
          <h3>AI Models (Ollama)</h3>
          {info.ollama_models?.length > 0 ? (
            <div className="conductor-table-wrap">
              <table className="conductor-table">
                <thead><tr><th>Model</th><th>Details</th><th>Size</th><th>Version</th><th>Status</th></tr></thead>
                <tbody>
                  {info.ollama_models.map(m => (
                    <tr key={m.name}>
                      <td className="conductor-name">{m.label}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{m.description} ({m.family})</td>
                      <td className="mono">{m.size_gb} GB</td>
                      <td className="mono">{m.version}</td>
                      <td><StatusBadge status={m.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="node-hub-empty">Ollama not running or no models loaded. Start with: ollama serve</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemModulesView;
